import { IVec3, Vec3 } from "@base/math/Vec3";
import { PointName, SunSailSides, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { getPointName, getPoleId } from "@biz/definitions/SunSailDefinitions.util";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";
import { ILocaContext } from "@biz/language/LocaContext";
import { IObstacleData } from "@biz/models/Sail2dDefinitions";

export type ICalculatedResult<T> = { value?: T, error?: string }
export type ICalculatedSideResult = ICalculatedResult<number>;

export class ObstacleHelper {
  static calcObstaclePosition(od: IObstacleData | undefined, axleLengthNetto: number | undefined, lc: ILocaContext): ICalculatedResult<IVec3> {
    if (!od)
      return { error: lc.t("Error_Obstacle_Infos_Missing") }
    if (!od.m3 || !od.m4)
      return { error: lc.t("Error_Obstacle_Add_Infos") }
    if (!axleLengthNetto)
      return { error: lc.t("Error_Obstacle_AxleLength_Missing") }
    const heightC = TriangleHelper.calcHeightC(od.m3, od.m4, axleLengthNetto);
    if (heightC === undefined)
      return { error: lc.t("Error_Obstacle_Position_Calculation_Failed") }
    const distToHeightC = Math.sqrt(od.m3 ** 2 - heightC ** 2)
    if (Number.isNaN(distToHeightC))
      return { error: lc.t("Error_Obstacle_Position_Calculation_Failed") }
    const x = distToHeightC / 1000;
    const y = heightC / 1000;
    switch (od.side) {
      case "AB": return { value: Vec3.from(x, 0, -y) }
      case "BC": return { value: Vec3.from(axleLengthNetto / 1000 - x, 0, -y) }
      case "AD": return { value: Vec3.from(x, 0, y) }
      case "CD": return { value: Vec3.from(axleLengthNetto / 1000 - x, 0, y) }
    }
    return { error: "Side not handled " + od.side }
  }

  static calcMeasureInfos(sunSailType: SunSailType, side: SunSailSides): {
    a1: string;
    a2: string;
    p: string;
    names: string[]
  } {
    const p1 = side[0] as PointName;
    const p2 = side[1] as PointName;

    const p1IsAxlePole = PoleLogic.isAxle(sunSailType, getPoleId(p1));
    const axlePole = p1IsAxlePole ? p1 : p2;
    const otherPole = p1IsAxlePole ? p2 : p1;
    const otherAxlePole = getPointName(Poles.getOtherPole(sunSailType, getPoleId(axlePole)));

    return {
      a1: axlePole,
      a2: otherAxlePole,
      p: otherPole,
      names: [
        `${otherPole}-${axlePole}' (1)`,
        `${axlePole}-${axlePole}' (2)`,
        `${axlePole}-X (3)`,
        `${otherAxlePole}-X (4)`,
      ]
    }
  }

  static calcObstacleSideLength(od: IObstacleData | undefined, axleLengthNetto: number | undefined, otherSideLength: number | IObstacleData | undefined, lc: ILocaContext): ICalculatedSideResult {
    if (!od)
      return { error: lc.t("Error_Obstacle_Infos_Missing") }
    if (!od.m1 || !od.m2)
      return { error: lc.t("Error_Obstacle_Add_Infos") }
    if (!axleLengthNetto)
      return { error: lc.t("Error_Obstacle_AxleLength_Missing") }
    const _otherSideLength = typeof otherSideLength === "number" ? otherSideLength : otherSideLength?.m1;
    if (!_otherSideLength)
      return { error: lc.t("Error_Obstacle_OppositeSide_Missing") }
    const _otherSideLengthM1 = typeof otherSideLength === "number" ? 0 : (otherSideLength?.m2 || 0);
    const heightC = TriangleHelper.calcHeightC(od.m1, _otherSideLength, axleLengthNetto - od.m2 - _otherSideLengthM1);
    if (heightC === undefined)
      return { error: lc.t("Error_Obstacle_Length_Calculation_Failed") }
    const distToHeightC = Math.sqrt(od.m1 ** 2 - heightC ** 2)
    const resultLength = Math.sqrt(heightC ** 2 + (distToHeightC + od.m2) ** 2)
    if (Number.isNaN(resultLength))
      return { error: lc.t("Error_Obstacle_Length_Calculation_Failed") }
    return {
      value: resultLength
    }
  }
}
